import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../css/css_pages/c_dashboard.css";
import Cookies from "js-cookie";
import config from "../../config";
import { useTranslation } from 'react-i18next';

// Helper function for deep comparison
const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const Dashboard = () => {
  // State declarations
  const [date, setDate] = useState(new Date());
  const [schedules, setSchedules] = useState([]);
  const [latestIssues, setLatestIssues] = useState([]);
  const [latestSchedules, setLatestSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [showAllSchedules, setShowAllSchedules] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const previousDataRef = useRef({
    schedules: [],
    latestIssues: [],
    latestSchedules: []
  });

  // Handle language change
  const handleLanguageChange = (e) => {
    const selectedLanguage = e.target.value;
    i18n.changeLanguage(selectedLanguage);
  };

  // Initialize data fetching
  useEffect(() => {
    const authToken = Cookies.get("authToken");
    if (!authToken) {
      setError(t('auth_error'));
      setLoading(false);
      return;
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch all required data
  const fetchData = async () => {
    try {
      setLoading(true);
      const token = Cookies.get("authToken");

      const [schedulesRes, issuesRes, latestSchedulesRes] = await Promise.all([
        fetch(`${config.API_URL}/client/get-all-schedules-by-customer-id`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${config.API_URL}/client/get-latest-reported-issues-for-dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${config.API_URL}/client/get-service-schedules-for-dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      // Handle all responses
      if (!schedulesRes.ok || !issuesRes.ok || !latestSchedulesRes.ok) {
        const errorData = await schedulesRes.json().catch(() => ({}));
        throw new Error(errorData.message || t('fetch_error'));
      }

      const [schedulesData, issuesData, latestSchedulesData] = await Promise.all([
        schedulesRes.json(),
        issuesRes.json(),
        latestSchedulesRes.json()
      ]);

      // Format schedules data
      const formattedSchedules = Array.isArray(schedulesData)
        ? schedulesData.map(schedule => ({
            ...schedule,
            scheduledDate: new Date(schedule.scheduled_date)
          }))
        : [];

      // Check if data has actually changed
      const hasChanged =
        !isEqual(formattedSchedules, previousDataRef.current.schedules) ||
        !isEqual(issuesData, previousDataRef.current.latestIssues) ||
        !isEqual(latestSchedulesData, previousDataRef.current.latestSchedules);

      if (hasChanged) {
        // Update previous data reference
        previousDataRef.current = {
          schedules: formattedSchedules,
          latestIssues: issuesData,
          latestSchedules: latestSchedulesData
        };

        // Update state only if data changed
        setSchedules(formattedSchedules);
        setLatestIssues(issuesData);
        setLatestSchedules(latestSchedulesData);
        setLastUpdated(new Date());
        setError(null);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(`${t('fetch_error')}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Get dot color based on schedule status
  const getDotColor = (status) => {
    switch (status) {
      case "pending": return "red";
      case "confirmed": return "#A6EAD1";
      case "in_progress": return "yellow";
      case "completed": return "green";
      case "closed": return "blue";
      case "proposed": return "#871F78";
      default: return "gray";
    }
  };

  // Get schedule dots for a specific date
  const getScheduleDots = (date) => {
    const schedulesForDate = schedules.filter(
      schedule => schedule.scheduledDate.toDateString() === date.toDateString()
    );

    return schedulesForDate.map((schedule, index) => (
      <div
        key={`${schedule.id}-${index}`}
        className="dot"
        style={{ backgroundColor: getDotColor(schedule.schedule_status) }}
      ></div>
    ));
  };

  // Handle schedule click
  const handleScheduleClick = (schedule) => {
    const selectedDate = new Date(schedule.scheduled_date);
    navigate(`/schedules/selectedDate=${selectedDate.toLocaleDateString("en-CA")}`);
  };

  // Toggle between showing all schedules and only the nearest 5
  const toggleAllSchedules = () => {
    setShowAllSchedules(!showAllSchedules);
  };

  // Filter and sort schedules to show only the nearest 5 by default
  const nearestSchedules = latestSchedules
    .filter(schedule => new Date(schedule.scheduled_date) >= new Date())
    .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))
    .slice(0, showAllSchedules ? latestSchedules.length : 5);

  if (loading) {
    return <div className="loading-message">{t('loading')}</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        {/* Language Dropdown */}
        <div className="language-selector">
          <select 
            onChange={handleLanguageChange} 
            value={i18n.language}
            className="language-dropdown"
          >
            <option value="en">English</option>
            <option value="si">සිංහල</option>
            <option value="ta">தமிழ்</option>
          </select>
        </div>
        
        {lastUpdated && (
          <span className="last-updated">
            {/* Last updated: {lastUpdated.toLocaleTimeString()} */}
          </span>
        )}
      </div>

      <div className="dashboard-intro"></div>

      {/* Calendar Section */}
      <section className="schedule">
        <h2>{t('service_schedules')}</h2>
        <div className="calendar-container">
          <Calendar
            onChange={setDate}
            value={date}
            className="calendar"
            tileContent={({ date, view }) => {
              if (view !== 'month') return null;

              const hasSchedules = schedules.some(
                schedule => schedule.scheduledDate.toDateString() === date.toDateString()
              );

              return (
                <div
                  className="tile-container"
                  onMouseEnter={() => setHoveredDate(date)}
                  onMouseLeave={() => setHoveredDate(null)}
                >
                  <div className="dot-container">{getScheduleDots(date)}</div>
                  {hoveredDate?.toDateString() === date.toDateString() &&
                    hasSchedules && (
                      <button
                        className="show-schedules-btn"
                        onClick={() =>
                          navigate(`/schedules/selectedDate=${date.toLocaleDateString("en-CA")}`)
                        }
                      >
                        {t('show')}
                      </button>
                    )}
                </div>
              );
            }}
          />
        </div>
      </section>

      {/* Reported Issues Section */}
      <section className="tasks">
        <h2>{t('reported_issues')}</h2>
        <div className="task-lists">
          {latestIssues.length > 0 ? (
            latestIssues.map((issue) => (
              <div
                key={issue.id}
                className="task-cards"
                style={{
                  backgroundColor:
                    issue.ticket_status === "pending" ? "#faafaf" :
                    issue.ticket_status === "in_progress" ? "#fff3cd" :
                    issue.ticket_status === "completed" ? "#E2F7D4" :
                    issue.ticket_status === "closed" ? "#F0D9E7" : "#f8f9fa",
                  color:
                    issue.ticket_status === "pending" ? "#2f0909" :
                    issue.ticket_status === "in_progress" ? "#856404" :
                    issue.ticket_status === "completed" ? "#155724" :
                    issue.ticket_status === "closed" ? "#1b1e21" : "#212529",
                }}
              >
                <div className="task-row">
                  <p className="task-issue">{issue.issue || t('no_task_name')}</p>
                  <p className="task-date">
                    {new Date(issue.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="no-data-message">{t('no_tasks_available')}</p>
          )}
        </div>
        <button
          className="add-task-btn"
          onClick={() => navigate("/Tasks")}
          aria-label={t('view_all_tasks')}
        >
          {t('show_tasks')}
        </button>
      </section>

      {/* Upcoming Schedules Section */}
      <section className="schedule-list">
        <h2>{t('upcoming_schedules')}</h2>
        <div className="schedule-lists">
          {nearestSchedules.length > 0 ? (
            nearestSchedules.map((schedule) => (
              <div
                key={schedule.id}
                className="schedule-cards"
                onClick={() => handleScheduleClick(schedule)}
                style={{
                  backgroundColor:
                    schedule.schedule_status === "pending" ? "#faafaf" :
                    schedule.schedule_status === "confirmed" ? "#d4edda" :
                    schedule.schedule_status === "in_progress" ? "#fff3cd" :
                    schedule.schedule_status === "completed" ? "#E2F7D4" :
                    schedule.schedule_status === "closed" ? "#D3D3D3" : "#f8f9fa",
                  color:
                    schedule.schedule_status === "pending" ? "#8B0000" :
                    schedule.schedule_status === "confirmed" ? "#155724" :
                    schedule.schedule_status === "in_progress" ? "#856404" :
                    schedule.schedule_status === "completed" ? "#155724" :
                    schedule.schedule_status === "closed" ? "#212529" : "#212529",
                  cursor: "pointer",
                }}
              >
                <div className="schedule-row">
                  <p className="schedule-issue">
                    {schedule.schedule_description || t('no_schedule_name')}
                  </p>
                  <p className="schedule-date">
                    {new Date(schedule.scheduled_date).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="no-data-message">{t('no_schedules_available')}</p>
          )}
        </div>
        <button
          className="add-schedule-btn"
          onClick={toggleAllSchedules}
          aria-label={showAllSchedules ? t('show_fewer_schedules') : t('show_more_schedules')}
        >
          {showAllSchedules ? t('show_less') : t('more')}
        </button>
      </section>
    </div>
  );
};

export default Dashboard;